import { useOneClickTradingV2 } from '@ui/features/OneClickTrading/hooks/useOneClickTradingV2';
import { useInvalidateLimitOrderQuery } from '@ui/features/Trade/hooks/useInvalidateQuery';
import { useDataChainId } from '@ui/hooks/datachain';
import { useMutationToast } from '@ui/hooks/useMutationToast';
import { useSimulateMutation } from '@ui/hooks/useSimulateMutation';
import { useTxnWriteContract } from '@ui/hooks/useTxnWriteContract';
import { pollV2IntentStatus } from '@ui/services/backend/oneClickV2Service';
import LimitOrderFacet from '@ui/services/contract/abis/LimitOrderFacet';
import { getContractAddress } from '@ui/services/contract/constants';
import { getLimitOrderFacet } from '@ui/services/contract/contract-instances/getLimitOrderFacet';
import type { Address } from '@ui/types/alias';
import { TxnMutateCallBackParams } from '@ui/types/tx';
import { extractErrorMessage } from '@ui/utils/extractContractError';
import { increaseGasLimit } from '@ui/utils/increaseGastLimit';
import { isNil, omit } from 'lodash-es';
import { useCallback, useMemo, useState } from 'react';
import { useAccount, usePublicClient } from 'wagmi';

import { useCollateralToken } from '../../TradeModule/store/useCollateralToken';
import { useOpenLimitOrderParams } from './useOpenLimitOrderParams';
import { useOpenLimitOrderToastOpts } from './useOpenLimitOrderToastOpts';

export const useOpenLimitOrder = ({
  onSuccess,
  onError,
  onSettled,
  onReceiptSettled,
}: TxnMutateCallBackParams) => {
  const chainId = useDataChainId();
  const { onErrorToastOpts, onMutateToastOpts, onSuccessToastOpts } =
    useOpenLimitOrderToastOpts();

  const invalidateLimitOrderQuery = useInvalidateLimitOrderQuery();

  const openLimitOrderParams = useOpenLimitOrderParams();
  const collateralToken = useCollateralToken((s) => s.useCollateralTokenValue());
  const isNativeCollateralToken = collateralToken?.isNative ?? false;

  const {
    isReady: canUseOneClickTradingV2,
    isSubmittingLimitOpen: isSubmittingLimitOpenV2,
    sendLimitOpen: sendLimitOpenV2,
  } = useOneClickTradingV2();
  const mutationToastInfo = useMutationToast();
  const [isAwaitingOneClickResult, setIsAwaitingOneClickResult] =
    useState(false);

  const { data, writeContract, isPending } = useTxnWriteContract({
    onSuccessToastOpts,
    onMutateToastOpts,
    onErrorToastOpts,
    onSuccess: () => {
      invalidateLimitOrderQuery();
      onSuccess?.();
    },
    onError,
    onSettled,
    onReceiptSettled,
  });

  const client = usePublicClient({ chainId });
  const { address: account } = useAccount();
  const { isSimulating, simulate } = useSimulateMutation();

  const write = useCallback(async () => {
    const leverUpAddress = getContractAddress('LeverUp', chainId);
    if (isNil(openLimitOrderParams) || isNil(leverUpAddress)) return;

    // OneClick V2 path (priority)
    if (canUseOneClickTradingV2 && !isNativeCollateralToken) {
      setIsAwaitingOneClickResult(true);
      mutationToastInfo.onMutate({
        title: 'In Progress',
        description: '⚡️ One-click V2 limit order in progress',
      });

      try {
        const omitValue = omit(openLimitOrderParams, [
          'value',
          'pythUpdateData',
          'oracleUpdateData',
        ]);

        const intentHash = await sendLimitOpenV2(omitValue);

        const tradingStatus = await pollV2IntentStatus(intentHash);
        if (!tradingStatus.success) {
          throw new Error(
            tradingStatus.reason ||
              'One-click V2 limit order execution failed',
          );
        }

        invalidateLimitOrderQuery();

        const txHash = tradingStatus.txnHash as Address;
        mutationToastInfo.onSuccess({
          title: '⚡️ Completed',
          description: 'Limit order placed successfully via one-click V2',
          txHash,
        });

        onSuccess?.();

        return txHash;
      } catch (error) {
        const message =
          error instanceof Error
            ? extractErrorMessage(error.message) ?? error.message
            : undefined;

        mutationToastInfo.onError({
          errorMsg: message,
          onErrorToastOpts: {
            description:
              'One-click V2 limit order failed, please try again',
          },
        });
        onError?.(error);
        throw error;
      } finally {
        mutationToastInfo.onSettled();
        setIsAwaitingOneClickResult(false);
        onSettled?.();
      }
    }

    // On-chain path
    const omitValue = omit(openLimitOrderParams, [
      'value',
      'pythUpdateData',
      'oracleUpdateData',
      'extraFee',
    ]);
    if (!client) return;

    let estimateGasResult;

    const { isSuccess } = await simulate(async () => {
      const contract = getLimitOrderFacet({
        chainId,
        publicClient: client,
      });
      estimateGasResult = await contract.estimateGas.openLimitOrderV2(
        [
          omitValue,
          openLimitOrderParams.oracleUpdateData,
          openLimitOrderParams.extraFee,
        ],
        {
          account: account,
          value: openLimitOrderParams.value,
        },
      );
    });
    if (!isSuccess) return;

    // Calculate gas limit with 10% buffer
    const gasLimit = increaseGasLimit(estimateGasResult);

    return writeContract({
      abi: LimitOrderFacet,
      address: leverUpAddress,
      functionName: 'openLimitOrderV2',
      args: [
        omitValue,
        openLimitOrderParams.oracleUpdateData,
        openLimitOrderParams.extraFee,
      ],
      value: openLimitOrderParams.value,
      gas: gasLimit,
    });
  }, [
    account,
    canUseOneClickTradingV2,
    isNativeCollateralToken,
    chainId,
    client,
    invalidateLimitOrderQuery,
    mutationToastInfo,
    onError,
    onSettled,
    onSuccess,
    openLimitOrderParams,
    sendLimitOpenV2,
    simulate,
    writeContract,
  ]);

  return useMemo(() => {
    return {
      data,
      writeContract: write,
      isPending:
        isPending ||
        isSimulating ||
        isSubmittingLimitOpenV2 ||
        isAwaitingOneClickResult,
    };
  }, [
    data,
    isPending,
    isSimulating,
    isSubmittingLimitOpenV2,
    isAwaitingOneClickResult,
    write,
  ]);
};

